/**
 * clientNavContext.js
 *
 * Helper para navegar a la ficha del cliente preservando contexto de retorno.
 * Usa sessionStorage (sobrevive F5 en la ficha, se limpia al consumir one-shot).
 *
 * Fase 1: infraestructura base.
 * Solo consumidor integrado: ClientsTab. Resto de orígenes intactos.
 *
 * Uso:
 *   import { navigateToClientWithReturn, consumeReturnContext, peekReturnContext } from '@/utils/clientNavContext';
 *
 *   // Origen:
 *   navigateToClientWithReturn(navigate, client.id, 'clientes:listado', {
 *     path: '/admin?tab=clientes',
 *     filters: { search, letraFiltro },
 *     scrollY: window.scrollY,
 *     sortBy, sortDir,
 *   });
 *
 *   // Destino (ClientNewProfile):
 *   const returnKey = searchParams.get('returnKey');
 *   const ctx = peekReturnContext(returnKey);
 *   if (ctx) { ... render botón Volver ... }
 *   // al click: const c = consumeReturnContext(returnKey); navigate(c.path);
 */

const PREFIX = 'clientNavReturn:';

export const saveReturnContext = (moduleKey, context) => {
  if (!moduleKey) return;
  try {
    sessionStorage.setItem(PREFIX + moduleKey, JSON.stringify({
      path: context.path || null,
      filters: context.filters || null,
      scrollY: context.scrollY ?? 0,
      sortBy: context.sortBy ?? null,
      sortDir: context.sortDir ?? null,
      savedAt: new Date().toISOString(),
    }));
  } catch (e) {
    console.warn('[clientNavContext] saveReturnContext failed:', e);
  }
};

export const peekReturnContext = (moduleKey) => {
  if (!moduleKey) return null;
  try {
    const raw = sessionStorage.getItem(PREFIX + moduleKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const consumeReturnContext = (moduleKey) => {
  if (!moduleKey) return null;
  try {
    const raw = sessionStorage.getItem(PREFIX + moduleKey);
    if (!raw) return null;
    sessionStorage.removeItem(PREFIX + moduleKey);
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const navigateToClientWithReturn = (navigate, clientId, moduleKey, context = {}) => {
  // Si no se pasa path/scrollY, se derivan de la ubicación actual. Como las
  // pestañas del CRM persisten en sessionStorage (useSessionTab), volver a la
  // misma URL restaura la pantalla y sub-pestaña de origen automáticamente.
  let autoPath = null;
  let autoScrollY = 0;
  try {
    if (typeof window !== 'undefined') {
      autoPath = window.location.pathname + window.location.search;
      autoScrollY = window.scrollY || 0;
    }
  } catch { /* noop */ }
  saveReturnContext(moduleKey, {
    path: context.path || autoPath,
    filters: context.filters || null,
    scrollY: context.scrollY ?? autoScrollY,
    sortBy: context.sortBy ?? null,
    sortDir: context.sortDir ?? null,
  });
  const qs = new URLSearchParams({ id: String(clientId), returnKey: moduleKey });
  navigate(`/admin/cliente-nuevo?${qs.toString()}`);
};
